import { useMemo, useState } from 'react'

import { Alert, Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import {
  ApprovalsValueObjectsApprovalCategoryEnum,
  DelegateApproverSelectorAccessItemDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface Props {
  category: ApprovalsValueObjectsApprovalCategoryEnum
  approverAccessId?: number
  field: Field
  startsOn: string
  finishOn: string
  employeeAccessId: number
}

export const DelegateApproverSelector = ({
  category,
  approverAccessId,
  field,
  startsOn,
  finishOn,
  employeeAccessId,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [selectedAccessId, setSelectedAccessId] = useState<number | undefined>(approverAccessId)

  const { loading: loadingApprovalFlows, approvalFlows } = useAccessApprovalFlows(
    category,
    employeeAccessId
  )

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const isLoading = loadingApprovalFlows
  const delegateApproverEnabled = approvalFlows?.length > 0

  const handleSelectAccess = (accessId: number | undefined) => {
    setSelectedAccessId(accessId)
    field.set(accessId)
  }

  const { data: accessItemData } = useQuery(DelegateApproverSelectorAccessItemDocument, {
    variables: { accessId: Number(selectedAccessId) },
    skip: !selectedAccessId,
  })

  const selectedAccessName = useMemo(
    () => accessItemData?.employees?.employeeNamesConnection?.nodes?.[0]?.fullName,
    [accessItemData]
  )

  if (isLoading || !delegateApproverEnabled) {
    return <></>
  }

  const startDate = stringToDate(startsOn, 'ui').format('ll')
  const endDate = stringToDate(finishOn, 'ui').format('ll')

  return (
    <Box gap="s4" paddingTop="s8">
      <Box flexDirection="row" gap="s4" alignItems="center">
        <Text size={'200'} color="grey1000">
          {i18n.t('approvals.delegate_approver.title')}
        </Text>
        <Text size={'200'} color="grey700">
          {i18n.t('approvals.delegate_approver.title_2')}
        </Text>
        <Tooltip.Alt
          attachment="top center"
          label={i18n.t('approvals.delegate_approver.description')}
          open={open}
          toggle={toggle}
        >
          <Icon.Medium icon="info-circle" color="grey800" />
        </Tooltip.Alt>
      </Box>
      <Box gap="s8">
        <EmployeeSelectorV2
          value={selectedAccessId}
          onChange={handleSelectAccess}
          useAccessId
          forcedFilters={{
            excludeCurrentEmployee: employeeAccessId === currentAccessId,
          }}
          messages={{
            placeholder: i18n.t('approvals.delegate_approver.empty_state'),
          }}
          options={{
            hideLabel: true,
          }}
        />

        {field.value && selectedAccessId && selectedAccessName && (
          <Alert color="warning">
            <Box flexDirection="row" justifyContent="center">
              <Text.Normal>
                {i18n.t('approvals.delegate_approver.delegate_approver_assigned', {
                  name: selectedAccessName ?? '',
                  start_date: startDate,
                  end_date: endDate,
                })}
              </Text.Normal>
            </Box>
          </Alert>
        )}
      </Box>
    </Box>
  )
}
